/**
 * PATH: packages/quote-builder/src/grid/useSheetGridFormulaCompose.ts
 * FILE_ID: QB-GRID-FORMULA-COMPOSE-001
 * PURPOSE: Capture-phase mousedown: insert A1 refs while composing "=…".
 * WHY: Keeps SheetGrid under the 500-line ceiling; owns Shift/Ctrl range
 *   insert + compose preview highlight refresh.
 * RELATED: refInsert.ts; SheetGrid.tsx.
 */
import { useEffect, type MutableRefObject, type RefObject } from 'react'

import { expandRange } from './a1'
import {
  insertRefText,
  isComposingFormula,
  type FormulaRefMode,
  type RefInsertSpan,
} from './refInsert'
import type { SheetGridRow } from './sheetGridTypes'

type GridApiLike = { refreshCells: (opts: { force: boolean }) => void }

export function useSheetGridFormulaCompose(args: {
  wrapperRef: RefObject<HTMLDivElement | null>
  apiRef: MutableRefObject<GridApiLike | null>
  liveRef: MutableRefObject<{
    rows: SheetGridRow[]
    getCellA1Ref?: (row: SheetGridRow, field: string) => string | null
  }>
  lastInsertRef: MutableRefObject<{
    el: HTMLInputElement
    span: RefInsertSpan
    valueAfter: string
  } | null>
  composeHighlightRef: MutableRefObject<Set<string>>
}) {
  const { wrapperRef, apiRef, liveRef, lastInsertRef, composeHighlightRef } = args

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const setComposeHighlight = (anchor: string, end: string | null) => {
      composeHighlightRef.current = new Set(expandRange(anchor, end ?? anchor))
      apiRef.current?.refreshCells({ force: true })
    }

    const handleMouseDown = (e: MouseEvent) => {
      const { rows: liveRows, getCellA1Ref: resolve } = liveRef.current
      if (!resolve) return

      const active = document.activeElement
      if (!(active instanceof HTMLInputElement)) return
      if (e.target instanceof Node && active.contains(e.target)) return
      const isInCellEditor = wrapper.contains(active)
      const isFormulaBar =
        active.dataset.testid === 'sheet-grid-formula-bar' ||
        active.hasAttribute('data-sheet-grid-formula-input')
      if (!isInCellEditor && !isFormulaBar) return
      if (!isComposingFormula(active.value)) return

      const target = e.target instanceof Element ? e.target : null
      const cellEl = target?.closest('.ag-cell')
      if (!cellEl || !wrapper.contains(cellEl)) return
      const colId = cellEl.getAttribute('col-id')
      if (!colId || colId === '__rownum') return
      const rowId = cellEl.closest('[row-id]')?.getAttribute('row-id')
      if (!rowId) return
      const row = liveRows.find((r) => r.id === rowId)
      if (!row) return
      const ref = resolve(row, colId)
      if (!ref) return

      e.preventDefault()
      e.stopPropagation()

      const mode: FormulaRefMode = e.shiftKey
        ? 'range'
        : e.ctrlKey || e.metaKey
          ? 'append'
          : 'replace'

      const last = lastInsertRef.current
      const span =
        last && last.el === active && last.valueAfter === active.value
          ? last.span
          : null
      const selStart = active.selectionStart ?? active.value.length
      const selEnd = active.selectionEnd ?? selStart
      const result = insertRefText(
        active.value,
        selStart,
        selEnd,
        ref,
        span,
        mode,
      )

      const setter = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        'value',
      )?.set
      if (setter) setter.call(active, result.next)
      else active.value = result.next
      active.dispatchEvent(new Event('input', { bubbles: true }))
      active.setSelectionRange(result.caret, result.caret)
      active.focus()
      lastInsertRef.current = {
        el: active,
        span: result.lastInsert,
        valueAfter: result.next,
      }
      setComposeHighlight(result.previewAnchor, result.previewEnd)
    }

    document.addEventListener('mousedown', handleMouseDown, true)
    return () => document.removeEventListener('mousedown', handleMouseDown, true)
  }, [apiRef, composeHighlightRef, lastInsertRef, liveRef, wrapperRef])
}
